import { useCallback, useEffect, useState } from "react";
import { Button, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  type Case,
  CommunicationStatus,
  DocumentStatus,
  DocumentType,
  Source,
} from "@/briefpoint-client";
import {
  FinishButton,
  FinishDraftButton,
  ReviewButton,
  StartButton,
} from "@/components/DocumentList/DocumentList";
import Loading from "@/components/Loading";
import { FormModal } from "@/components/Modals/Modal";
import type {
  ClientResponse,
  DocumentClient,
  JurisdictionResponse,
  ViewCommunicationResponse,
} from "@/lib/types";
import { dispatchSort, toDateOrNow } from "@/lib/utils";
import "./DocumentTable.scss";
import { useHistory } from "react-router-dom";
import { DocumentRowWithReviewClient } from "@/components/DocumentList/DocumentRowWithReviewClient";
import { useCommunicationApi } from "@/hooks/useApi";
import { sortCommunications } from "@/lib/utils";
import DocumentsTableHeadersBridge from "./DocumentsTableHeadersBridge";

const ProcessingButton = () => (
  <Button variant="outline-secondary" size="sm" disabled>
    <Spinner animation="border" size="sm" /> Processing...
  </Button>
);

export function Action(document: DocumentClient) {
  if (document.source === Source.InternalGenerated) {
    return <FinishDraftButton document={document} />;
  }

  if (!document.status) return <ProcessingButton />;

  switch (document.status as DocumentStatus) {
    case DocumentStatus.InfoConfirmed:
      return (
        <FinishButton
          documentId={document.id}
          type={document.info?.documentType ?? DocumentType.Unknown}
        />
      );
    case DocumentStatus.Complete:
      return (
        <ReviewButton
          documentId={document.id}
          type={document.info?.documentType ?? DocumentType.Unknown}
        />
      );
    case DocumentStatus.Processed:
      return <StartButton documentId={document.id} />;
    case DocumentStatus.Unknown:
    case DocumentStatus.Uploaded:
    case DocumentStatus.Processing:
    case DocumentStatus.Deleted:
    case DocumentStatus.Error:
      return <ProcessingButton />;
    default:
      return DocumentStatus[document.status];
  }
}

interface Props {
  documents: DocumentClient[] | undefined;
  _case?: Case;
  jurisdiction?: JurisdictionResponse;
  keyPrefix: string;
  clients?: ClientResponse[];
  uploadDocAction?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  updateDocument: (d: DocumentClient) => Promise<void>;
  archiveDocument: (d: DocumentClient) => Promise<void>;
  downloadForm: (
    document: DocumentClient,
    formId: string,
    formTypeName: string,
  ) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;
  isUploading?: boolean;
}

export default function DocumentsTable({
  documents,
  _case,
  jurisdiction,
  keyPrefix: key,
  clients,
  uploadDocAction,
  updateDocument,
  archiveDocument,
  downloadForm,
  downloadRequest,
  isUploading = false,
}: Props) {
  const [sortColumn, setSortColumns] = useState("uploadDate");
  const [sortDirection, setSortDirection] = useState<
    "descending" | "ascending"
  >("descending");
  const [showEditDueDate, setShowEditDueDate] = useState<
    DocumentClient | undefined
  >();
  const [dueDateOverride, setDueDateOverride] = useState<string | undefined>();
  const history = useHistory();
  const communicationApi = useCommunicationApi();
  const [communications, setCommunications] = useState<
    ViewCommunicationResponse[] | undefined
  >();

  function changeSort(column: string) {
    if (column === sortColumn) {
      setSortDirection(
        sortDirection === "ascending" ? "descending" : "ascending",
      );
    } else {
      setSortColumns(column);
      setSortDirection("descending");
    }
  }

  function addDueDateDays(days?: number) {
    const existing = toDateOrNow(
      showEditDueDate?.info?.dueDateOverridden ||
        showEditDueDate?.info?.dueDate,
    );
    const newDate = days
      ? existing.plus({ days })
      : existing.plus({ month: 1 });

    setDueDateOverride(newDate.toFormat("yyyy-MM-dd"));
  }

  function closeDueDateModal() {
    setShowEditDueDate(undefined);
    setDueDateOverride(undefined);
  }

  async function saveDocument(showEditDueDate: DocumentClient): Promise<void> {
    if (showEditDueDate.info) {
      showEditDueDate.info.dueDateOverridden = dueDateOverride;
    }
    await updateDocument(showEditDueDate);
    closeDueDateModal();
  }

  function shouldShowInfo(
    doc: DocumentClient,
    details: string,
    placeholder = "-",
  ) {
    return doc.status === DocumentStatus.Complete ||
      doc.status === DocumentStatus.InfoConfirmed ||
      doc.source === Source.InternalGenerated
      ? details
      : placeholder;
  }

  function createReview(doc: DocumentClient) {
    history.push(`/documents/${doc.id}/client-feedback`);
  }

  const loadCommunication = useCallback(async () => {
    const coms = (await communicationApi.communicationGetFirmCommunications({
      lowerBound: CommunicationStatus.Pending,
      upperBound: CommunicationStatus.Complete,
    })) as ViewCommunicationResponse[];
    setCommunications(coms);
  }, [communicationApi]);

  useEffect(() => {
    if (!communications) {
      loadCommunication();
    }
  }, [communications, loadCommunication]);

  async function handleRemoveClientAccess(
    communication: ViewCommunicationResponse,
  ) {
    await communicationApi.communicationUpdateCommunication({
      // biome-ignore lint/style/noNonNullAssertion: communication.id is not null
      id: communication.id!,
      putClientCommunication: {
        ...communication,
        sharedWithObjectType: undefined,
        sharedWithObjectId: undefined,
        status: CommunicationStatus.Closed,
      },
    });
    setCommunications(communications?.filter((c) => c.id !== communication.id));
  }

  async function handleReopenCommunication(
    communication: ViewCommunicationResponse,
  ) {
    await communicationApi.communicationUpdateCommunication({
      // biome-ignore lint/style/noNonNullAssertion: communication.id is not null
      id: communication.id!,
      putClientCommunication: {
        ...communication,
        status: CommunicationStatus.Open,
      },
    });
    setCommunications((c) => {
      const update = [...(c || [])];
      const item = c?.find((x) => x.id === communication.id);
      if (item) {
        item.status = CommunicationStatus.Open;
      }
      return update;
    });
  }

  function getCommunicationForDoc(doc: DocumentClient) {
    return communications
      ?.filter((c) => c.objectId === doc.id)
      ?.sort(sortCommunications)?.[0];
  }

  const updateDocumentName = async (
    newName: string,
    document: DocumentClient,
  ): Promise<void> => {
    const updatedDocument = { ...document, name: newName };
    await updateDocument(updatedDocument);
  };

  return (
    <Loading className="documentsLoading" isLoading={documents === undefined}>
      {documents?.length && jurisdiction ? (
        <>
          <Row>
            <Table
              data-testid="document-list-table"
              id="document-list-table"
              striped
              hover
              style={{ marginLeft: "12px" }}
            >
              <DocumentsTableHeadersBridge
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                changeSort={changeSort}
                jurisdiction={jurisdiction}
              />

              <tbody>
                {documents
                  ?.sort((a, b) =>
                    dispatchSort(
                      a,
                      b,
                      sortColumn,
                      sortDirection === "ascending",
                      undefined,
                      clients,
                    ),
                  )
                  .map((d) => {
                    const client = clients?.find((c) => c.id === d.clientId);
                    return (
                      <DocumentRowWithReviewClient
                        _case={_case}
                        doc={d}
                        communication={getCommunicationForDoc(d)}
                        archiveDocument={archiveDocument}
                        editDueDate={
                          jurisdiction?.supportsDueDates
                            ? () => setShowEditDueDate(d)
                            : undefined
                        }
                        downloadForm={downloadForm}
                        downloadRequest={downloadRequest}
                        removeClientAccess={handleRemoveClientAccess}
                        client={client}
                        reopenCommunication={handleReopenCommunication}
                        createReview={createReview}
                        key={`${key}-${d.id}`}
                        respondingParty={shouldShowInfo(
                          d,
                          d.info?.respondingPartyName ?? "",
                          "",
                        )}
                        propoundingParty={shouldShowInfo(
                          d,
                          d.info?.propoundingPartyName ?? "",
                          "",
                        )}
                        updateDocumentName={(name) =>
                          updateDocumentName(name, d)
                        }
                      />
                    );
                  })}
              </tbody>
            </Table>
          </Row>
          <FormModal
            show={!!showEditDueDate}
            title={`${showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate ? "Edit" : "Set"} Due Date`}
            size="sm"
            confirmText="Confirm"
            cancelText="Cancel"
            onClose={closeDueDateModal}
            onConfirm={() => showEditDueDate && saveDocument(showEditDueDate)}
          >
            {(showEditDueDate?.info?.dueDateOverridden ||
              showEditDueDate?.info?.dueDate) && (
              <div className="add-x-days">
                Add{" "}
                <Button variant="link" onClick={() => addDueDateDays(7)}>
                  7 days
                </Button>
                ,{" "}
                <Button variant="link" onClick={() => addDueDateDays(14)}>
                  14 days
                </Button>
                ,{" "}
                <Button onClick={() => addDueDateDays()} variant="link">
                  1 month
                </Button>
              </div>
            )}
            <Form.Control
              type="date"
              className="date-picker"
              value={
                dueDateOverride ||
                showEditDueDate?.info?.dueDateOverridden ||
                showEditDueDate?.info?.dueDate ||
                undefined
              }
              onChange={(e) => setDueDateOverride(e.target.value)}
            />
          </FormModal>
        </>
      ) : (
        <p className="d-flex mt-3 gap-1 align-items-center">
          No Documents yet,{" "}
          {uploadDocAction ? (
            <Button
              variant="link"
              onClick={uploadDocAction}
              className="p-0"
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload one Now!"}
            </Button>
          ) : (
            "Draft one now from the menu above."
          )}
        </p>
      )}
    </Loading>
  );
}
